import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { adoptWindowParams } from './lib/appWindows'

// A window opened for a project (lib/appWindows.ts): which window it is and its project, kept for this window only.
adoptWindowParams()
import { applyTheme, getTheme } from './lib/theme'
import { DEFAULT_LOCALE, getLocale, loadLocale } from './lib/i18n'
import './styles/tokens.css'
import './styles/base.css'
import './styles/components.css'
import './styles/bughunt.css'
import './styles/scene.css'
import './styles/scenery.css'
import './styles/day.css'
import './styles/remote.css'
import './styles/reports.css'
import './styles/docs.css'
import './styles/navigation.css'
import './styles/workspace.css'
import './styles/onboarding.css'
import './styles/future.css'

// Applied before first paint so the page does not flash the wrong theme on load.
applyTheme(getTheme())
document.documentElement.lang = getLocale()

/**
 * Query defaults.
 *
 * Staleness is generous and polling is off, because SignalR is what keeps this screen current —
 * §14 asks for updates without polling, and a refetch interval alongside a live socket would be
 * doing the same job twice. What remains is refetch on focus, which covers the case a socket
 * cannot: a laptop that was asleep and has just been opened.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchInterval: false,
      retry: (failureCount, error) => {
        // Retrying an authorization failure just produces the same answer more slowly.
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || status === 404) return false
        return failureCount < 2
      },
    },
  },
})

// The active language first (lib/i18n.ts): drawn before it is here, every label would be its key. Unreachable, the
// default language; neither, the keys — never a blank screen.
void loadLocale(getLocale())
  .catch(() => loadLocale(DEFAULT_LOCALE))
  .catch(() => undefined)
  .then(() =>
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </StrictMode>,
    ),
  )
